import { useEffect, useRef } from "react";

interface UseDialogOptions {
  show: boolean;
  onClose: () => void;
  /** When true the hook also listens for clicks on the `<dialog>` element
   *  itself (i.e. the backdrop) and calls `onClose`. Default: false. */
  closeOnBackdropClick?: boolean;
}

/**
 * Manages a native `<dialog>` element: show/hide, cancel-event handling,
 * and optional backdrop-click dismissal.
 *
 * Returns a `ref` to attach to the `<dialog>`.
 */
export function useDialog({ show, onClose, closeOnBackdropClick = false }: UseDialogOptions) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  // Show / hide the dialog
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (show && !dialog.open) {
      dialog.showModal();
    } else if (!show && dialog.open) {
      dialog.close();
    }
  }, [show]);

  // Handle native cancel (Escape) and optional backdrop click
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const handleCancel = (e: Event) => {
      e.preventDefault();
      onClose();
    };

    dialog.addEventListener("cancel", handleCancel);

    let handleBackdropClick: ((e: MouseEvent) => void) | undefined;
    if (closeOnBackdropClick) {
      handleBackdropClick = (e: MouseEvent) => {
        if (e.target === dialog) onClose();
      };
      dialog.addEventListener("click", handleBackdropClick);
    }

    return () => {
      dialog.removeEventListener("cancel", handleCancel);
      if (handleBackdropClick) {
        dialog.removeEventListener("click", handleBackdropClick);
      }
    };
  }, [onClose, closeOnBackdropClick]);

  return dialogRef;
}
